import { useEffect } from 'react';

import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormProvider } from '@/components/react-hook-form';

import { useManageIntegrationDataContext } from '../../context/manageIntegrationData.context';
import { EntityLinkTable } from './components/EntityLinkTable';
import { EntityLinkForm } from './hooks/useEntityLinkForm';
import { useDirectHoldingsLinkRows } from './hooks/useLinkPaneRows';

export interface DirectHoldingsLinkPaneProps {
  form: EntityLinkForm;
  integrationProviderName: string;
}

/**
 * "Link holdings": personal accounts and their integration data.
 *
 * The form belongs to the provider; this renders it and keeps it fed. The
 * query lives here rather than alongside the form so it only runs once the
 * pane is opened, and runs again whenever the user comes back to it.
 */
export function DirectHoldingsLinkPane({
  form,
  integrationProviderName,
}: DirectHoldingsLinkPaneProps) {
  const { householdId, isSaving } = useManageIntegrationDataContext();
  const { rows, loading } = useDirectHoldingsLinkRows();
  const { formMethods, seed } = form;

  useEffect(() => {
    if (loading) return;
    seed(rows);
  }, [loading, rows, seed]);

  return (
    <FormProvider {...formMethods}>
      <FormFieldsDisabledProvider isDisabled={isSaving}>
        <EntityLinkTable
          rows={rows}
          householdId={householdId}
          integrationProviderName={integrationProviderName}
          entityColumnHeader="Luminary holding groups"
          loading={loading}
        />
      </FormFieldsDisabledProvider>
    </FormProvider>
  );
}
